import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Monitor, Globe, ShieldCheck, LogOut } from 'lucide-react';

const ModeSelectorPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Force dark theme on the splash regardless of the user's saved
  // preference — light-theme rewrites `text-white/*` classes to dark text
  // which washes out the entire mode-card list on this page.
  useEffect(() => {
    const html = document.documentElement;
    const hadLight = html.classList.contains('light-theme');
    if (hadLight) html.classList.remove('light-theme');
    return () => {
      if (hadLight) html.classList.add('light-theme');
    };
  }, []);

  const modes = [
    {
      id: 'demo',
      icon: Monitor,
      title: 'Demo Mode',
      description: 'Explore the platform with sample data across 16 industry verticals',
      action: () => navigate('/demo'),
      color: 'from-teal-500 to-cyan-600',
      bg: 'hover:border-teal-400',
      tag: 'Sample Data',
    },
    {
      id: 'domains',
      icon: Globe,
      title: 'Domain Portal',
      description: 'Go directly to a customer domain dashboard — view calls, analytics and reports',
      action: () => navigate('/admin/domains'),
      color: 'from-emerald-500 to-teal-600',
      bg: 'hover:border-emerald-400',
      tag: 'Live Customers',
    },
    {
      id: 'admin',
      icon: ShieldCheck,
      title: 'Admin',
      description: 'System administration: add domains, manage sync, configure the platform',
      action: () => navigate('/admin'),
      color: 'from-slate-600 to-slate-800',
      bg: 'hover:border-slate-400',
      tag: 'System Control',
    },
  ];

  return (
    <div className="min-h-screen bg-[#0A0F1C] flex flex-col items-center justify-center p-6">
      <div className="mb-12 text-center">
        <div className="inline-block bg-white rounded-2xl px-6 py-3 mb-4 shadow-lg">
          <img
            src="https://customer-assets.emergentagent.com/job_call-quality-grade/artifacts/ehb854bc_1.Landscape.png"
            alt="Yabbit VoiceFabric"
            className="h-12 w-auto object-contain"
          />
        </div>
        <p className="text-white text-2xl font-bold tracking-tight">
          Yabbit <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-cyan-500">VoiceFabric</span>
        </p>
        <p className="text-slate-400 text-sm mt-2">
          Welcome, <span className="text-white font-medium">{user?.name}</span>
        </p>
      </div>

      {/* Mode Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full max-w-4xl">
        {modes.map((mode) => {
          const Icon = mode.icon;
          return (
            <button
              key={mode.id}
              data-testid={`mode-${mode.id}`}
              onClick={mode.action}
              className={`group relative text-left p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur transition-all duration-300 ${mode.bg} hover:bg-white/10 hover:shadow-2xl hover:-translate-y-1`}
            >
              <span className="absolute top-4 right-4 text-xs font-medium text-white/40 bg-white/10 px-2 py-0.5 rounded-full">
                {mode.tag}
              </span>
              <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${mode.color} mb-5 shadow-lg`}>
                <Icon className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">{mode.title}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">{mode.description}</p>
              <div className="mt-6 flex items-center text-sm font-medium text-white/60 group-hover:text-white transition-colors">
                Enter <span className="ml-1 group-hover:translate-x-1 transition-transform">→</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Logout */}
      <button
        onClick={() => { logout(); navigate('/login'); }}
        className="mt-12 flex items-center gap-2 text-slate-500 hover:text-white transition-colors text-sm"
        data-testid="mode-logout-btn"
      >
        <LogOut className="w-4 h-4" />
        Sign out
      </button>
    </div>
  );
};

export default ModeSelectorPage;
